import React from 'react';
import { Link } from 'react-router-dom';
import { pillarById } from '../utils/format';

// Local, PillarCard-only date formatter — deliberately not added to the
// shared utils/format.js while GameCard is being redesigned concurrently in
// another file. Mirrors the short-date shape ScoreHistoryChart already uses
// (`{ month: 'short', day: 'numeric' }`) for a consistent "Jul 27" feel.
function formatShortDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function PillarCard({ pillar, to }) {
  const meta = pillarById(pillar.id);
  const accent = meta?.accent || 'var(--accent-primary)';
  const measured = pillar.score != null;
  const lastMeasured = formatShortDate(pillar.lastAssessedAt);
  const due = pillar.assessment?.overdue;
  // A pillar with no instruments behind it is a score slot, not a destination.
  // Rendering it as a link would produce a button whose target 404s.
  //
  // Trend/delta indicators and a "Baseline N" footnote are omitted
  // intentionally: the backend hardcodes trend/delta to flat/0 for every
  // pillar and never returns a baseline value, so rendering either would
  // show invented movement (see docs/g-score-api-contract.md). The footnote
  // instead states the one true fact the API gives us about timing — when
  // the pillar was last assessed — rather than being left empty.
  //
  // The due/overdue badge IS restored (unlike trend/delta) — it's backed by
  // real data (assessment.overdue, assessment.nextAvailableAt) and is the
  // only thing on the card that tells a learner which pillars they can
  // assess today. It reads "Due now" when there's a startable destination
  // and "Coming soon" when the pillar is a score slot with no instrument —
  // same split as before. Warning-toned (not accent green): green on this
  // system is reserved for progress/completion/current-location/primary
  // action, and "due" is none of those — it's a needs-attention state.
  const Wrapper = to ? Link : 'div';
  const restBackground = measured ? 'var(--bg-elevated)' : 'var(--bg-secondary)';
  const restBorderColor = measured ? 'var(--border-subtle)' : 'var(--border-hover)';
  const wrapperProps = to
    ? {
        to,
        onMouseEnter: (e) => {
          e.currentTarget.style.borderColor = 'var(--border-hover)';
          e.currentTarget.style.background = 'var(--card-bg-hover)';
        },
        onMouseLeave: (e) => {
          e.currentTarget.style.borderColor = restBorderColor;
          e.currentTarget.style.background = restBackground;
        },
      }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      style={{
        display: 'block',
        textDecoration: 'none',
        background: restBackground,
        border: measured ? '1px solid var(--border-subtle)' : '1px dashed var(--border-hover)',
        borderRadius: 12,
        padding: '14px 14px 13px',
        color: 'var(--text-primary)',
        boxShadow: measured ? 'var(--shadow-sm)' : 'none',
        transition: 'border-color var(--transition-fast), background var(--transition-fast)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 6,
          marginBottom: 12,
          minHeight: 32,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 2,
            marginTop: 4,
            flex: 'none',
            boxSizing: 'border-box',
            background: measured ? accent : 'transparent',
            border: measured ? 'none' : '1.5px solid var(--border-strong)',
          }}
        />
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.25,
            minWidth: 0,
            color: measured ? 'var(--text-primary)' : 'var(--text-secondary)',
          }}
        >
          {meta?.label || pillar.id}
        </span>
      </div>

      {/* The due badge sits in the slot the mockup gives the trend delta,
          beside the score, rather than in the header row: six pillars share
          one row, so the title needs the header's full width to wrap into
          the 32px it is allotted. Wraps under the score when even this row
          runs out of room. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 7,
          rowGap: 4,
          marginBottom: 10,
        }}
      >
        <span
          style={{
            fontSize: 27,
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: '-0.03em',
            color: measured ? 'var(--text-primary)' : 'var(--text-tertiary)',
          }}
        >
          {measured ? pillar.score : '—'}
        </span>
        {due && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              lineHeight: 1.3,
              padding: '2px 6px',
              borderRadius: 4,
              whiteSpace: 'nowrap',
              color: to ? 'var(--warning)' : 'var(--text-tertiary)',
              background: to ? 'var(--warning-bg)' : 'transparent',
            }}
          >
            {to ? 'Due now' : 'Coming soon'}
          </span>
        )}
      </div>

      <div
        style={{
          height: 5,
          borderRadius: 3,
          marginBottom: 8,
          overflow: 'hidden',
          background: measured
            ? 'var(--bg-tertiary)'
            : 'repeating-linear-gradient(90deg, var(--bg-tertiary) 0 6px, transparent 6px 10px)',
        }}
      >
        {measured && (
          <div
            style={{
              height: '100%',
              width: `${Math.max(4, Math.min(100, pillar.score))}%`,
              background: accent,
              borderRadius: 3,
              transition: 'width 0.4s ease',
            }}
          />
        )}
      </div>

      <p style={{ margin: 0, fontSize: 11, color: 'var(--text-tertiary)' }}>
        {measured ? (lastMeasured ? `Last measured ${lastMeasured}` : ' ') : 'Not measured yet'}
      </p>
    </Wrapper>
  );
}
